import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { confirmDialog } from '@/lib/confirmDialog'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'

function WikiVersionHistory({ teamId, onRollback, onClose }) {
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [rollbackProcessing, setRollbackProcessing] = useState(false)

  const fetchVersions = async () => {
    try {
      const { data, error } = await supabase
        .from('wiki_versions')
        .select('*')
        .eq('team_id', teamId)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) throw error

      setVersions(data || [])
    } catch (err) {
      console.error('Failed to fetch version history:', err)
      toast.error('Failed to load version history')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVersions()
  }, [teamId])

  const handleRollback = async (version) => {
    const confirmed = await confirmDialog(
      `Rollback wiki to ${new Date(version.created_at).toLocaleString()}?`,
      {
        confirmText: 'Rollback',
        cancelText: 'Cancel',
        description: 'This will restore the wiki to this previous state. Current items will be replaced.'
      }
    )

    if (!confirmed) return

    setRollbackProcessing(true)

    try {
      // Delete current wiki items
      const { error: deleteError } = await supabase
        .from('wiki_items')
        .delete()
        .eq('team_id', teamId)

      if (deleteError) throw deleteError

      // Restore from snapshot
      const snapshotData = version.snapshot_data
      if (Array.isArray(snapshotData) && snapshotData.length > 0) {
        const { error: insertError } = await supabase
          .from('wiki_items')
          .insert(snapshotData.map(item => ({
            ...item,
            id: undefined, // Let DB generate new IDs
            created_at: new Date().toISOString()
          })))

        if (insertError) throw insertError
      }

      toast.success('Wiki rolled back successfully')
      onRollback?.()
      onClose?.()
    } catch (err) {
      console.error('Failed to rollback:', err)
      toast.error('Failed to rollback wiki')
    } finally {
      setRollbackProcessing(false)
    }
  }

  const renderPreview = (version) => {
    const data = version.snapshot_data
    if (!Array.isArray(data)) return <p className="text-brand-slate text-sm">No data</p>

    return (
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {data.slice(0, 5).map((item, idx) => (
          <div key={idx} className="p-2 bg-brand-cream rounded text-sm">
            <p className="font-medium text-brand-charcoal">{item.title}</p>
            <p className="text-brand-slate text-xs truncate">{item.content}</p>
          </div>
        ))}
        {data.length > 5 && (
          <p className="text-xs text-brand-slate text-center">
            ...and {data.length - 5} more items
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="max-w-4xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-bold">Wiki Version History</h2>
          <button
            onClick={onClose}
            className="text-brand-slate hover:text-brand-charcoal text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-brand-slate">
            Loading version history...
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-8 text-brand-slate">
            <p>No version history yet.</p>
            <p className="text-sm mt-2">
              Versions are created automatically when AI updates the wiki.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {versions.map((version) => {
              const isSelected = selectedVersion?.id === version.id
              const changesSummary = version.ai_changes_summary

              return (
                <div
                  key={version.id}
                  className={`p-4 border-2 rounded-lg ${
                    isSelected ? 'border-brand-green bg-green-50' : 'border-border'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={version.created_by === 'ai' ? 'secondary' : 'success'}>
                          {version.created_by === 'ai' ? 'AI Update' : 'Manual Save'}
                        </Badge>
                        <span className="text-sm text-brand-slate">
                          {new Date(version.created_at).toLocaleString()}
                        </span>
                      </div>
                      
                      {changesSummary && (
                        <div className="text-sm text-brand-slate mb-2">
                          {changesSummary.auto_applied > 0 && (
                            <span className="mr-3">
                              ✓ {changesSummary.auto_applied} auto-applied
                            </span>
                          )}
                          {changesSummary.queued > 0 && (
                            <span>⏸ {changesSummary.queued} queued</span>
                          )}
                        </div>
                      )}

                      {changesSummary?.summary && (
                        <p className="text-sm text-brand-charcoal">
                          {changesSummary.summary}
                        </p>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => setSelectedVersion(isSelected ? null : version)}
                        className="text-sm px-3 py-1.5"
                      >
                        {isSelected ? 'Hide' : 'Preview'}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => handleRollback(version)}
                        disabled={rollbackProcessing}
                        className="text-sm px-3 py-1.5"
                      >
                        {rollbackProcessing ? 'Rolling back...' : 'Rollback'}
                      </Button>
                    </div>
                  </div>

                  {isSelected && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <h4 className="font-medium text-brand-charcoal mb-2">
                        Wiki State at This Time:
                      </h4>
                      {renderPreview(version)}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-border text-center">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default WikiVersionHistory

